import React, { useState } from 'react';
import { selectTrialTier, getFirm } from '../services/firmService';
import { formatTrialEnd } from '../utils/subscription';

const TrialTierSelectionModal = ({ firmId, firmProfile, onSelected }) => {
  const trialEndLabel = formatTrialEnd(firmProfile?.subscription?.trialEnds);
  const [selecting, setSelecting] = useState('');
  const [error, setError] = useState('');

  const handleSelect = async (tier) => {
    if (selecting) return;
    setSelecting(tier);
    setError('');
    try {
      await selectTrialTier(firmId, tier);
      const result = await getFirm(firmId);
      if (result.success) {
        onSelected(result.firm);
      } else {
        throw new Error('Could not reload firm profile after tier selection');
      }
    } catch (e) {
      console.error('[TrialTierSelection] Failed to set tier:', e);
      setError('Could not save your selection. Please check your connection and try again.');
      setSelecting('');
    }
  };

  const tiers = [
    {
      id: 'solo',
      name: 'Solo',
      icon: '⚖️',
      tagline: 'For solo practitioners building their book of business.',
      blurb: 'Full case management, AI Paralegal, billing, client portal, and WhatsApp notifications. Best for chambers of one.',
      highlights: ['100 cases', '1 user', 'AI Paralegal', 'Billing & retainers', 'Client portal'],
    },
    {
      id: 'firm',
      name: 'Firm',
      icon: '🏛️',
      tagline: 'For growing chambers with a team.',
      blurb: 'Everything in Solo, plus the full template library, analytics, team hub, calendar sync, and email notifications. Most chambers start here.',
      highlights: ['300 cases', '10 users', 'Templates library', 'Analytics & reports', 'Team hub'],
      recommended: true,
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      icon: '👑',
      tagline: 'For established firms that want the Autonomous Paralegal Agent.',
      blurb: 'Everything in Firm, plus the full Autonomous Agent, AI automation, custom playbooks, custom branding, CSV export, and unlimited everything.',
      highlights: ['Unlimited cases', 'Unlimited users', 'Autonomous Agent', 'AI automation', 'Custom branding'],
    },
  ];

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(8, 10, 16, 0.96)', backdropFilter: 'blur(12px)',
      zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '2rem', overflowY: 'auto', fontFamily: "'DM Sans', sans-serif",
    }}>
      <div style={{ maxWidth: '1100px', width: '100%' }}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>👋</div>
          <h1 style={{ color: '#FFD700', fontSize: '1.9rem', margin: '0 0 0.5rem', fontWeight: 700, fontFamily: "'Playfair Display', serif" }}>
            Welcome to FirmAssist, Counsel
          </h1>
          <p style={{ color: '#a8adb7', fontSize: '1rem', margin: 0, maxWidth: '640px', marginLeft: 'auto', marginRight: 'auto', lineHeight: 1.6 }}>
            Choose the tier that best fits your practice. Your trial runs free until <strong style={{ color: '#e8eaed' }}>{trialEndLabel}</strong> — you can switch tiers anytime in Admin → Subscription. No card required.
          </p>
        </div>

        {error && (
          <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '10px', padding: '0.85rem 1rem', marginBottom: '1.25rem', color: '#ef4444', fontSize: '0.9rem', textAlign: 'center' }}>
            {error}
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(290px, 1fr))', gap: '1.25rem' }}>
          {tiers.map(tier => (
            <div key={tier.id} style={{
              background: tier.recommended ? 'linear-gradient(180deg, rgba(212,168,83,0.08), rgba(255,255,255,0.02))' : 'rgba(255,255,255,0.03)',
              border: tier.recommended ? '2px solid rgba(212,168,83,0.5)' : '1px solid rgba(255,255,255,0.08)',
              borderRadius: '16px', padding: '1.75rem', position: 'relative',
              display: 'flex', flexDirection: 'column',
            }}>
              {tier.recommended && (
                <div style={{ position: 'absolute', top: '-12px', left: '50%', transform: 'translateX(-50%)', background: 'linear-gradient(135deg, #d4a853, #e8c875)', color: '#080a10', padding: '0.25rem 0.85rem', borderRadius: '20px', fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Most Popular
                </div>
              )}
              <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>{tier.icon}</div>
              <h3 style={{ color: '#e8eaed', fontSize: '1.4rem', margin: '0 0 0.3rem', fontWeight: 700 }}>{tier.name}</h3>
              <div style={{ color: '#d4a853', fontSize: '0.85rem', marginBottom: '0.75rem', fontStyle: 'italic' }}>{tier.tagline}</div>
              <p style={{ color: '#a8adb7', fontSize: '0.88rem', lineHeight: 1.55, margin: '0 0 1.1rem' }}>{tier.blurb}</p>
              <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 1.5rem', flex: 1 }}>
                {tier.highlights.map((h, i) => (
                  <li key={i} style={{ color: '#e8eaed', fontSize: '0.85rem', padding: '0.35rem 0', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ color: '#10b981' }}>✓</span> {h}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => handleSelect(tier.id)}
                disabled={!!selecting}
                style={{
                  width: '100%', padding: '0.85rem', borderRadius: '12px', border: 'none',
                  background: selecting === tier.id ? 'rgba(212,168,83,0.4)' : 'linear-gradient(135deg, #d4a853, #e8c875)',
                  color: '#080a10', fontWeight: 700, fontSize: '0.95rem',
                  cursor: selecting ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
                  transition: 'all 0.2s', opacity: selecting && selecting !== tier.id ? 0.4 : 1,
                }}
              >
                {selecting === tier.id ? 'Activating…' : `Start ${tier.name} Trial`}
              </button>
            </div>
          ))}
        </div>

        <p style={{ textAlign: 'center', color: '#6b7080', fontSize: '0.78rem', marginTop: '2rem', lineHeight: 1.6 }}>
          You can switch tiers any time until {trialEndLabel}. After that date you'll be asked to choose a paid plan to continue.
        </p>
      </div>
    </div>
  );
};

export default TrialTierSelectionModal;
